import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/redux';
import {
  loginUser,
  teamLoginUser,
  registerUser,
  forgotPassword,
  resetPassword,
  changePassword,
  updateProfile,
  refreshToken,
  restoreAuth,
  logout,
  teamUserLogout,
  clearError as clearAuthError,
} from '@/store/redux/slices/authSlice';
import type {
  LoginCredentials,
  RegisterCredentials,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  UpdateProfileRequest,
} from '@/types/auth';

// Custom hook that provides the same interface as AuthContext but uses Redux
export const useReduxAuth = () => {
  const dispatch = useAppDispatch();
  const authState = useAppSelector((state) => state.auth);

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      await dispatch(loginUser(credentials));
    },
    [dispatch]
  );

  const teamLogin = useCallback(
    async (teamId: string, credentials: LoginCredentials) => {
      await dispatch(teamLoginUser({ teamId, credentials }));
    },
    [dispatch]
  );

  const register = useCallback(
    async (credentials: RegisterCredentials) => {
      await dispatch(registerUser(credentials));
    },
    [dispatch]
  );

  const authLogout = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const teamUserLogoutAction = useCallback(() => {
    dispatch(teamUserLogout());
  }, [dispatch]);

  const forgotPasswordAction = useCallback(
    async (request: ForgotPasswordRequest) => {
      await dispatch(forgotPassword(request));
    },
    [dispatch]
  );

  const resetPasswordAction = useCallback(
    async (request: ResetPasswordRequest) => {
      await dispatch(resetPassword(request));
    },
    [dispatch]
  );

  const changePasswordAction = useCallback(
    async (request: ChangePasswordRequest) => {
      await dispatch(changePassword(request));
    },
    [dispatch]
  );

  const updateProfileAction = useCallback(
    async (request: UpdateProfileRequest) => {
      await dispatch(updateProfile(request));
    },
    [dispatch]
  );

  const refreshTokenAction = useCallback(async () => {
    await dispatch(refreshToken());
  }, [dispatch]);

  const restoreAuthAction = useCallback(async () => {
    await dispatch(restoreAuth());
  }, [dispatch]);

  const clearError = useCallback(() => {
    dispatch(clearAuthError());
  }, [dispatch]);

  // Helper function to check if user should see teams page
  const shouldShowTeamsPage = useCallback(() => {
    return authState.user?.isTeamUser === true;
  }, [authState.user]);

  return {
    // State
    user: authState.user,
    token: authState.token,
    isAuthenticated: authState.isAuthenticated,
    loading: authState.loading,
    error: authState.error,
    
    // Actions
    login,
    teamLogin,
    register,
    authLogout,
    teamUserLogout: teamUserLogoutAction,
    forgotPassword: forgotPasswordAction,
    resetPassword: resetPasswordAction,
    changePassword: changePasswordAction,
    updateProfile: updateProfileAction,
    refreshToken: refreshTokenAction,
    restoreAuth: restoreAuthAction,
    clearError,
    shouldShowTeamsPage,
  };
};
